'use client';

import { QRCodeSVG } from 'qrcode.react';

export interface VBCQRCodeProps {
  /**
   * Full vCard 3.0 text to encode (`BEGIN:VCARD\r\nVERSION:3.0\r\n...\r\nEND:VCARD\r\n`).
   * Phones recognize the embedded vCard payload on scan and prompt to save
   * the contact natively — no browser detour.
   */
  vcardContent: string;
  /** Overline label rendered above the QR. */
  label?: string;
}

/**
 * QR code that homeowners scan with their phone camera to save the vendor
 * to their contacts.
 *
 * The encoded payload is the full vCard text — iOS Camera, Android camera,
 * and most third-party scanner apps recognize `BEGIN:VCARD…` directly and
 * show the native contact preview sheet on tap. No browser detour, no
 * server round-trip, no API hit.
 *
 * Client component because `qrcode.react` is browser-only (renders SVG via
 * the DOM). Theme tokens are used for the overline label; the QR pattern
 * itself uses qrcode.react's default black-on-white (the colors represent
 * the encoded data, not brand affordance).
 *
 * Error-correction level "M" handles the larger payload (~200 bytes for a
 * typical vendor's vCard, vs ~65 bytes for the prior URL-based encoding)
 * cleanly at the 140px display size. If phones fail to scan at certain
 * vendor profiles (long contact name + long company name), drop to "L" or
 * bump `size`.
 */
export function VBCQRCode({
  vcardContent,
  label = 'Save to your contacts',
}: VBCQRCodeProps) {
  return (
    <div>
      <h3
        className="overline mb-3"
        style={{ color: 'var(--brand-primary, #014F8A)' }}
      >
        {label}
      </h3>
      <QRCodeSVG
        value={vcardContent}
        size={140}
        level="M"
        title="Scan to save this vendor to your contacts"
      />
    </div>
  );
}
